import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Loader } from "@/components/ui/loader";
import { FormProvider, useForm } from "react-hook-form";
import {
  useGetOrganizationsQuery,
  useUpdateOrganizationMutation,
  useUpdateOrganizationStyleGuideMutation,
} from "@/api/organizations-admin";
import { useUpdateFileAccessMutation } from "@/api/files";
import type { EsgBoxConfig } from "@/types/organization";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { toast } from "react-toastify";
import { useGetFileDetailsQuery } from "@/api/files";
import { AiModelsTab } from "@/components/modals/organizationSettings/components/AiModelsTab";
import { EsgPortalTab } from "@/components/modals/organizationSettings/components/EsgPortalTab";
import { GeneralTab } from "@/components/modals/organizationSettings/components/GeneralTab";
import { StyleGuideTab } from "@/components/modals/organizationSettings/components/StyleGuideTab";
import {
  getApiErrorMessage,
  getEmptyStyleGuideValues,
  getStyleGuidePayload,
  getStyleGuideValues,
  parseSsoDomains,
} from "@/components/modals/organizationSettings/helpers";
import { useLlmModelSettings } from "@/components/modals/organizationSettings/hooks/useLlmModelSettings";
import type { OrganizationFormValues, StyleGuideFormValues } from "@/components/modals/organizationSettings/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const OrganizationSettingsModal = ({ open, onOpenChange }: Props) => {
  const { data: organizations, isLoading: isLoadingOrg } = useGetOrganizationsQuery(undefined, {
    skip: !open,
  });
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<string | null>(null);
  const organization = React.useMemo(
    () => organizations?.find(org => org.id === selectedOrganizationId) ?? organizations?.[0],
    [organizations, selectedOrganizationId],
  );

  const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
  const [updateStyleGuide, { isLoading: isUpdatingStyleGuide }] = useUpdateOrganizationStyleGuideMutation();
  const [updateFileAccess] = useUpdateFileAccessMutation();

  const generalForm = useForm<OrganizationFormValues>({
    defaultValues: {
      name: "",
      features: [],
      enabled2fa: false,
      twoFactorPrimaryChannel: "sms",
      dateFormat: "US",
      timeFormat: "12h",
      ssoMode: "password",
      ssoDomainsText: "",
      contractFamilyDivisionsText: "",
    },
  });
  const { reset, watch } = generalForm;

  const styleGuideForm = useForm<StyleGuideFormValues>({
    defaultValues: getEmptyStyleGuideValues(),
  });
  const { reset: resetStyleGuide } = styleGuideForm;

  const [esgBoxes, setEsgBoxes] = React.useState<EsgBoxConfig[]>([]);
  const { data: sharedFolders } = useGetFileDetailsQuery({ depth: 1 }, { skip: !open });

  const availableFolders = React.useMemo(() => {
    if (!sharedFolders || sharedFolders.type !== "folder") return [];
    return Object.values(sharedFolders.items)
      .filter(item => item.type === "folder")
      .map(item => ({ id: item.id, name: item.name }));
  }, [sharedFolders]);

  React.useEffect(() => {
    if (!open) {
      setSelectedOrganizationId(null);
      return;
    }
    if (!organizations?.length) return;
    setSelectedOrganizationId(current =>
      current && organizations.some(org => org.id === current) ? current : organizations[0].id,
    );
  }, [open, organizations]);

  React.useEffect(() => {
    if (open && organization) {
      reset({
        name: organization.name,
        features: organization.features,
        enabled2fa: organization.enabled2fa,
        twoFactorPrimaryChannel: organization.twoFactorPrimaryChannel ?? "sms",
        dateFormat: organization.dateFormat,
        timeFormat: organization.timeFormat,
        ssoMode: organization.ssoMode,
        ssoDomainsText: (organization.ssoDomains ?? []).join(", "),
        contractFamilyDivisionsText: (organization.contractFamilyDivisions ?? []).join("\n"),
      });
      setEsgBoxes(organization.esgPortalConfig ?? []);
      resetStyleGuide(getStyleGuideValues(organization));
    }
  }, [open, reset, resetStyleGuide, organization]);

  const llmModelSettings = useLlmModelSettings({ open, organizationId: organization?.id });

  const onSubmit = async (data: OrganizationFormValues) => {
    if (!organization) return;

    if (data.features.includes("esg_portal") && esgBoxes.some(box => !box.folderId)) {
      toast.error("Each ESG box must have a folder selected");
      return;
    }

    const { ssoDomainsText, contractFamilyDivisionsText, ...rest } = data;
    const ssoDomains = parseSsoDomains(ssoDomainsText);
    const contractFamilyDivisions = contractFamilyDivisionsText
      .split("\n")
      .map(value => value.trim())
      .filter(Boolean);

    if (
      contractFamilyDivisions.length > 50 ||
      contractFamilyDivisions.some(value => value.length > 100) ||
      new Set(contractFamilyDivisions.map(value => value.toLocaleLowerCase())).size !== contractFamilyDivisions.length
    ) {
      toast.error("Enter up to 50 unique division names, one per line (maximum 100 characters each)");
      return;
    }

    if (rest.ssoMode === "sso" && ssoDomains.length === 0) {
      toast.error("SSO mode 'SSO only' requires at least one email domain");
      return;
    }

    try {
      await updateOrganization({
        id: organization.id,
        data: { ...rest, ssoDomains, esgPortalConfig: esgBoxes, contractFamilyDivisions },
      }).unwrap();

      const allFolderIds = [...new Set(esgBoxes.map(box => box.folderId).filter(Boolean))];
      await Promise.all(allFolderIds.map(fse_id => updateFileAccess({ fse_id, access: "public" }).unwrap()));

      toast.success("Organization settings updated");
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update organization:", error);
      toast.error(getApiErrorMessage(error) ?? "Failed to update organization settings");
    }
  };

  const onStyleGuideSubmit = async (data: StyleGuideFormValues) => {
    if (!organization) return;

    try {
      await updateStyleGuide({
        id: organization.id,
        data: getStyleGuidePayload(data),
      }).unwrap();
      toast.success("Style guide updated");
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update style guide:", error);
      toast.error("Failed to update style guide");
    }
  };

  const onCancel = () => onOpenChange(false);

  const selectedFeatures = watch("features") || [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          <div className="flex items-start justify-between border-b pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">Organization Settings</Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500">
                Manage your organization's settings and features
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </Dialog.Close>
          </div>

          {isLoadingOrg ? (
            <div className="flex h-64 items-center justify-center">
              <Loader className="h-8 w-8 text-gray-400" />
            </div>
          ) : !organization ? (
            <div className="flex h-64 items-center justify-center">
              <p className="text-gray-500">Unable to load organization settings</p>
            </div>
          ) : (
            <>
              {organizations && organizations.length > 1 && (
                <div className="mt-4 space-y-2">
                  <Label htmlFor="organization-select">Organization</Label>
                  <select
                    id="organization-select"
                    value={organization?.id ?? ""}
                    onChange={event => setSelectedOrganizationId(event.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <Tabs defaultValue="general" className="mt-4">
                <TabsList>
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="style-guide">Style Guide</TabsTrigger>
                  <TabsTrigger value="ai-models">AI Models</TabsTrigger>
                  {selectedFeatures.includes("esg_portal") && <TabsTrigger value="esg-portal">ESG Portal</TabsTrigger>}
                </TabsList>
                <TabsContent value="general">
                  <FormProvider {...generalForm}>
                    <GeneralTab onSubmit={onSubmit} onCancel={onCancel} isUpdating={isUpdating} />
                  </FormProvider>
                </TabsContent>
                <TabsContent value="style-guide">
                  <FormProvider {...styleGuideForm}>
                    <StyleGuideTab
                      onSubmit={onStyleGuideSubmit}
                      onCancel={onCancel}
                      isUpdating={isUpdatingStyleGuide}
                    />
                  </FormProvider>
                </TabsContent>
                <TabsContent value="ai-models">
                  <AiModelsTab llmModelSettings={llmModelSettings} onCancel={onCancel} />
                </TabsContent>
                {selectedFeatures.includes("esg_portal") && (
                  <TabsContent value="esg-portal">
                    <FormProvider {...generalForm}>
                      <EsgPortalTab
                        esgBoxes={esgBoxes}
                        setEsgBoxes={setEsgBoxes}
                        availableFolders={availableFolders}
                        onSubmit={onSubmit}
                        onCancel={onCancel}
                        isUpdating={isUpdating}
                      />
                    </FormProvider>
                  </TabsContent>
                )}
              </Tabs>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
